import axios from 'axios'

import { logout, refreshAuthentication } from '@/auth/authentication'
import {
  getToken,
  isTokenSoonExpired,
  setToken,
} from '@/auth/authentication-jwt'
import store from '@/store'
import { addTrailingSlash } from '@/utils'

// Track if we're currently refreshing to prevent loops
let isRefreshing = false
let pendingRequests = []

/**
 * Utility to determine if notifications should be shown
 */
const doNotify = (error) => {
  let notify = true
  const headers = error?.config?.headers || {}
  if ('notify' in headers && headers.notify === false) {
    notify = false
  }
  return notify
}

/**
 * All interceptors organized by feature
 */
export const interceptors = {
  urlNormalise: (config, apiUrl) => {
    if (!config.url) return config
    const resolved = new URL(config.url, addTrailingSlash(apiUrl))
    resolved.pathname = addTrailingSlash(resolved.pathname)
    config.url = resolved.toString()
    return config
  },

  /**
   * Authentication interceptors
   */
  auth: {
    /**
     * Adds authentication token to request headers
     */
    addToken: (config) => {
      let token = getToken()
      config.headers['Authorization'] = `Bearer ${token}`
      return config
    },

    /**
     * Refreshes the token if it's going to expire soon
     */
    refreshToken: async (config) => {
      // Skip token check for token-refresh requests to avoid loops
      if (
        isRefreshing ||
        config.url.includes('token-refresh') ||
        config.url.includes('token-verify')
      ) {
        return config
      }

      const token = getToken()
      if (!token) {
        return config
      }

      if (isTokenSoonExpired(token)) {
        if (!isRefreshing) {
          isRefreshing = true

          try {
            const newToken = await refreshAuthentication(token)
            setToken(newToken)

            // Process pending requests with the new token
            pendingRequests.forEach((callback) => callback(newToken))
            pendingRequests = []

            // Update this request's token
            config.headers['Authorization'] = `Bearer ${newToken}`
          } catch (error) {
            pendingRequests.forEach((callback) => callback(null, error))
            pendingRequests = []
            throw error
          } finally {
            isRefreshing = false
          }
        } else {
          // Queue this request until the token is refreshed
          return new Promise((resolve, reject) => {
            pendingRequests.push((newToken, error) => {
              if (error) {
                reject(error)
              } else {
                config.headers['Authorization'] = `Bearer ${newToken}`
                resolve(config)
              }
            })
          })
        }
      }

      return config
    },

    /**
     * Handles 401 Unauthorized responses by logging out
     * Distinguishes between genuine auth failures and server downtime
     */
    handleLogout: async (error) => {
      const is401 = error?.response?.status === 401
      const isMissingOrInvalidToken =
        error.name === 'missing-token' || error.name === 'invalid-token'

      // Don't log out if server is down - likely a temporary restart
      if (store.state.isServerDown) {
        return Promise.reject(error)
      }

      // Check if this is a genuine auth error from Django with a proper response body
      // Django REST Framework JWT returns specific error messages for token issues:
      // - "Signature has expired"
      // - "Error decoding signature"
      // - "Invalid token" etc.
      const hasAuthErrorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.non_field_errors

      // Only log out for genuine auth failures:
      // 1. Missing or invalid token in our code
      // 2. 401 with a proper error message from backend (not just connection refused)
      if (isMissingOrInvalidToken || (is401 && hasAuthErrorMessage)) {
        await store.dispatch('auth/reset')
        logout()
      }

      // If we get a 401 without a proper error message, it's likely a server issue
      // Don't log out in that case - just reject the error and let connection handler deal with it
      return Promise.reject(error)
    },
  },

  /**
   * Request cancellation
   */
  cancellation: {
    /**
     * Adds cancellation token to request
     */
    addCancelToken: (config, requestManager) => {
      let cancellable = true
      if ('cancellable' in config) cancellable = config.cancellable

      if (cancellable === true) {
        // Generate cancel token source
        let source = axios.CancelToken.source()
        // Set cancel token on this request
        config.cancelToken = source.token
        // Add to request manager
        requestManager.addRequestTokenSource(source)

        // Set up cleanup when the request is complete
        const originalComplete = config.complete
        config.complete = function () {
          requestManager.removeRequestTokenSource(source)
          if (originalComplete) {
            originalComplete.apply(this, arguments)
          }
        }
      }

      return config
    },

    /**
     * Handles cancelled requests
     */
    handleCancelled: (error) => {
      if (error && axios.isCancel(error)) {
        return new Promise(() => {}) // Never resolves, effectively cancels the request chain
      }
      return Promise.reject(error)
    },
  },

  /**
   * Connection status handling
   */
  connection: {
    /**
     * Updates connection status on successful response
     */
    handleSuccess: (response) => {
      if (store.state.isServerDown) store.commit('setIsServerDown', false)
      return response
    },

    /**
     * Handles connection errors
     * Detects server downtime from network errors or connection issues
     * Note: 500 errors are NOT considered server down (can be expected)
     */
    handleError: (error) => {
      const isNetworkError = error.message === 'Network Error'
      const isConnectionError =
        error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND'
      const isCORSError =
        error.code === 'ERR_NETWORK' || (isNetworkError && !error.response)

      // Mark server as down ONLY for true connection failures
      // (Network errors, connection refused, CORS errors - not 500s)
      if (
        (isNetworkError || isConnectionError || isCORSError) &&
        !store.state.isServerDown
      ) {
        console.error('Server connection error:', error)
        store.commit('setIsServerDown', true)
      }

      return Promise.reject(error)
    },
  },

  /**
   * Timeout handling
   */
  timeout: {
    /**
     * Handles request timeouts
     */
    handleTimeout: (error) => {
      if (error.code === 'ECONNABORTED') {
        if (doNotify(error)) {
          const message = `${error.code}: ${error.message}`
          store.commit('notify', { type: 'error', message })
        }
      }
      return Promise.reject(error)
    },
  },

  /**
   * Blob handling
   */
  blob: {
    /**
     * Handles blob to JSON conversion for error responses
     */
    convertBlobToJson: (error) => {
      if (
        error.response &&
        error.request.responseType === 'blob' &&
        error.response.data instanceof Blob &&
        error.response.data.type &&
        error.response.data.type.toLowerCase().indexOf('json') !== -1
      ) {
        return new Promise((resolve, reject) => {
          let reader = new FileReader()
          reader.onload = () => {
            error.response.data = JSON.parse(reader.result)
            resolve(Promise.reject(error))
          }
          reader.onerror = () => {
            reject(error)
          }
          reader.readAsText(error.response.data)
        })
      }
      return Promise.reject(error)
    },
  },
}

/**
 * Individual setup functions for each feature
 */
export const setupFeature = {
  urlNormalise: (instance, apiUrl) => {
    instance.interceptors.request.use((config) =>
      interceptors.urlNormalise(config, apiUrl),
    )
  },

  auth: (instance) => {
    instance.interceptors.request.use(interceptors.auth.addToken)
    instance.interceptors.request.use(interceptors.auth.refreshToken)
    instance.interceptors.response.use(
      (response) => response,
      interceptors.auth.handleLogout,
    )
  },

  cancellation: (instance, requestManager) => {
    instance.interceptors.request.use((config) =>
      interceptors.cancellation.addCancelToken(config, requestManager),
    )
    instance.interceptors.response.use(
      (response) => response,
      interceptors.cancellation.handleCancelled,
    )
  },

  connection: (instance) => {
    instance.interceptors.response.use(
      interceptors.connection.handleSuccess,
      interceptors.connection.handleError,
    )
  },

  timeout: (instance) => {
    instance.interceptors.response.use(
      (response) => response,
      interceptors.timeout.handleTimeout,
    )
  },

  blob: (instance) => {
    instance.interceptors.response.use(
      (response) => response,
      interceptors.blob.convertBlobToJson,
    )
  },
}
